﻿// needs data from server represented in an i18n variable 
import React, {useEffect, useState, useRef} from "react";
import { Tooltip } from 'react-tooltip'
import * as _ from "lodash";
import {IntlProvider, FormattedMessage, FormattedNumber} from 'react-intl'
import NavOption from "./NavOption"
import NavSearch from "./NavSearch"
import localeData from "./navlocalisations.json"

interface NavigationProps
{
    apiUrl:string,
    regionCode:string,
    activeArea:string
}

const NavigationCore = (props:NavigationProps) => {
    const menuRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Nav>(null);
    const [activeSubmenu, setActiveSubmenu] = useState("")
    const [floating, setFloating] = useState<boolean>(false);
    const [searchActive, setSearchActive] = useState<boolean>(false);
    const [listening, setListening] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobile, setMobile] = useState(false);
    const [messages, setMessages] = useState({});
    
    const blurred = () => {
        if(!mobile)
            handleSetActiveSubMenu("")
    }
    
    const clickedAway = () => {
        setSearchActive(false)
        handleSetActiveSubMenu("")
    }

    const debounceCloseActiveSubmenu = _.debounce(() => {
        setActiveSubmenu("");
    }, 300);
    
    const handleSetActiveSubMenu = (val:string) => {
        
        // console.log("HANDLING",val)
        
        if(!searchActive) {
            if(!mobile) {
                if(val == "") {
                    debounceCloseActiveSubmenu()
                } else {
                    debounceCloseActiveSubmenu.cancel();
                    setActiveSubmenu(val)
                    setSearchActive(false)
                }
            }else {
                if(val == "" || val == activeSubmenu) {
                    setActiveSubmenu("")
                    setSearchActive(false)
                } else {
                    setActiveSubmenu(val)
                    setSearchActive(false)
                }
            }
        }
        
    }
    
    const searchButtonHandler = (e:React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();//Stops the a tag doing the default action
        setActiveSubmenu(""); //Close any open submenus
        
        setSearchActive(!searchActive);
    }

    useEffect(() => {
        setLoading(true)
        fetch(`${props.apiUrl}?timestamp=${new Date().getTime()}`)
            .then((response) => response.json())
            .then((data) => {
                setLoading(false)
                setData(data)
                
                
            })
            .catch((e) => {
                setLoading(false)
                setError("Error fetching navigation")
            })
    }, [])

    useEffect(() => {
        // Split locales with a region code
        const languageWithoutRegionCode = props.regionCode.toLowerCase().split(/[_-]+/)[0];
        
        const messages =
            localeData[languageWithoutRegionCode] ||
            localeData[props.regionCode] ||
            localeData.en;
        
        setMessages(messages)
    }, [props.regionCode])

    useEffect(() => {
        const scrollEvent = () => {
            setFloating(window.scrollY > 0);
        }

        window.addEventListener("scroll", scrollEvent, { passive: true })

        return () => window.removeEventListener("scroll", scrollEvent)
    }, []);
    
    useEffect(() => {
        if(mobileOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
    }, [mobileOpen])

    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            
            if(window.innerWidth < 1100) {
                setMobile(true)
            }
            else {
                setMobile(false)
            }
        }

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, []); // Empty array ensures that effect is only run on mount


    useEffect(listenForOutsideClicks(
        listening,
        setListening,
        menuRef,
        clickedAway,
    ));

    function listenForOutsideClicks(listening, setListening, menuRef, clickedAway) {
        return () => {
            if (listening) return;
            if (!menuRef.current) return;
            setListening(true);
            [`click`, `touchstart`].forEach((type) => {
                document.addEventListener(`click`, (evt) => {
                    if (menuRef.current.contains(evt.target)) return;
                    clickedAway();
                });
            });
        }
    }

    return (
        <IntlProvider locale={props.regionCode} messages={messages} defaultLocale="en">
        <div className={`float_filler ${floating ? "active" : ""}`}>
        </div>
        <div 
            ref={menuRef} 
            className={`menu_nav ${floating ? "floating" : ""} ${mobile ? "mobile" : ""} ${mobileOpen ? "mobile_open" : ""}`} 
            onMouseLeave={() => blurred()}
        >
            <div className="mobile_bar">
                <a href="/" aria-label="Go to the home page">
                    <div className="nav_logo"></div>
                </a>
                <div>
                    <a
                        className={"search_button"}
                        aria-label={"Open search"}
                        onClick={(e) => searchButtonHandler(e)}>Search</a>
                    <div
                        className={`hamburger ${mobileOpen ? "open" : ""}`}
                        onClick={() => setMobileOpen(!mobileOpen)}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
                
            </div>
            <NavSearch IsActive={searchActive} locale={props.regionCode} />
            <Tooltip id="menu-tooltip" />

            {!mobile && 
            <div className={`menu_container ${mobileOpen ? "mobile_open" : ""} ${mobile ? "mobile" : ""}`}>
                <a href="/" aria-label="Go to the home page">
                    <div className="nav_logo"></div>
                </a>

                {!mobile && 
                <>
                <ul className="nav_options">
                    {loading && "Loading..."}
                    {error && error}
                    {data != null && 
                        data.NavOptions !== null ? data.NavOptions
                            .sort((a, b) => a.NavOptionOrder - b.NavOptionOrder)
                            .filter((navOption) => navOption.Enabled === true)
                            .map((navOption) => {
                                return <NavOption 
                                    key={navOption.NavOptionOrder + navOption.LinkUrl} 
                                    {...navOption}
                                    handleActivation={(val) => handleSetActiveSubMenu(val)}
                                    IsActive={activeSubmenu === navOption.LinkUrl}
                                    IsMobile={false}
                                />
                            }) : null
                    }
                    
                </ul>
                <div className="unw__account">
                    <div className={`unw__search ${searchActive ? "active" : ""}`}>
                        {/*<a href="" className="unw__btn medium btnlogin">Log in</a>*/}
                        <a 
                            className={"search_button"} 
                            aria-label={"Open search"} 
                            onClick={(e) => searchButtonHandler(e)}>Search</a>
                        
                    </div>
                </div>
                </>
                }
            </div>
            }

            {mobile && mobileOpen && 
            <ul className={"mobile_menu"}>
                {loading && "Loading..."}
                {error && error}
                {data != null &&
                data.NavOptions !== null ? data.NavOptions
                    .sort((a, b) => a.NavOptionOrder - b.NavOptionOrder)
                    .filter((navOption) => navOption.Enabled === true)
                    .map((navOption) => {
                        return <NavOption
                            key={navOption.NavOptionOrder + navOption.LinkUrl}
                            {...navOption}
                            handleActivation={(val) => handleSetActiveSubMenu(val)}
                            IsActive={activeSubmenu === navOption.LinkUrl}
                            IsMobile={mobile}
                        />
                    }) : null
                }

            </ul>
            }

        </div>
        </IntlProvider>
    )
}

export default NavigationCore;